import React, { PropsWithChildren, TouchEvent } from 'react';
import { useRef } from 'react';

export enum SwipeDirection {
    Top,
    Right,
    Bottom,
    Left
}

type SwipeableProps = PropsWithChildren<{
    className?: string;
    swipeOffset?: number;
    waitForEnd?: boolean;
    onTouch?: () => void;
    onSwipe: (swipeDirection: SwipeDirection) => void;
}>;

export const Swipeable = ({ className, swipeOffset, waitForEnd, onTouch, onSwipe, children }: SwipeableProps) => {
    const defaultSwipeOffset = 50;
    const touchStartPositionXRef = useRef(0);
    const touchStartPositionYRef = useRef(0);

    const handleTouchStart = (e: TouchEvent<HTMLElement>) => {
        touchStartPositionXRef.current = e.touches[0]?.clientX || 0;
        touchStartPositionYRef.current = e.touches[0]?.clientY || 0;

        if (onTouch) {
            onTouch();
        }
    };

    const handleSwipe = (currentPositionX: number, currentPositionY: number) => {
        if (currentPositionX < touchStartPositionXRef.current - (swipeOffset ?? defaultSwipeOffset)) {
            onSwipe(SwipeDirection.Left);
        }
        if (currentPositionX > touchStartPositionXRef.current + (swipeOffset ?? defaultSwipeOffset)) {
            onSwipe(SwipeDirection.Right);
        }
        if (currentPositionY < touchStartPositionYRef.current - (swipeOffset ?? defaultSwipeOffset)) {
            onSwipe(SwipeDirection.Top);
        }
        if (currentPositionY > touchStartPositionYRef.current + (swipeOffset ?? defaultSwipeOffset)) {
            onSwipe(SwipeDirection.Bottom);
        }
    };

    const handleTouchMove = (e: TouchEvent<HTMLElement>) => handleSwipe(e.touches[0]?.clientX, e.touches[0]?.clientY);
    const handleTouchEnd = (e: TouchEvent<HTMLElement>) => handleSwipe(e.changedTouches[0]?.clientX, e.changedTouches[0]?.clientY);

    return (
        <div
            className={className}
            onTouchStart={handleTouchStart}
            onTouchMove={waitForEnd ? undefined : handleTouchMove}
            onTouchEnd={waitForEnd ? handleTouchEnd : undefined}
        >
            {children}
        </div>
    );
};
